import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import Avatar from '../../../../../avatar/Avatar';
import ReviewerUsersPanel from '../ReviewerUsersPanel/ReviewerUsersPanel';
import ReviewerUsersPanelHeader from '../ReviewerUsersPanelHeader';
import ReviewersPanelSearch from '../ReviewersPanelSearch';
import Chip from '@mui/material/Chip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers } from '../../../../../../services/user/user.service';
import { updateFilter } from '../../../../../../store/actions/filter.actions';
import { updateAssessmentsBuilderReviewers } from '../../../../../../store/actions/dataEntryAssessmentBuilderActions';
import { ASSESSMENT_TYPE_CUSTOM } from '../../../constants/typeAssessment';
import './GlobalReviewersPanel.css';

const getInitialReviewers = (reviewersList = []) => {
  return reviewersList.map((item) => ({ ...item, display: true }));
};

const GlobalReviewersPanel = ({ reviewersList = [], isConfirming = false, setIsConfirming }) => {
  const list = getInitialReviewers(reviewersList);
  const [isMounted, setIsMounted] = useState(false);
  const [reviewers, setReviewers] = useState(list);
  const [userInputSearch, setUserInputSearch] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [reviewerInputSearch, setReviewerInputSearch] = useState('');
  const filter = useSelector((state) => state?.filter?.assessmentReviewers?.filter || {});
  const open = useSelector((state) => state?.filter?.assessmentReviewers?.open || false);
  const userRoles = useSelector((state) => state?.session?.user?.roles || []);
  const dispatch = useDispatch();

  const applyUsersDispersedSearch = (searchTerm = '', originalList = []) => {
    const term = searchTerm?.toLowerCase();

    const updatedList = originalList?.map((item) => {
      const codeMatch = item?.code?.toLowerCase()?.includes(term);
      const fullName = `${item.firstName} ${item.lastName}`.toLowerCase();
      const emailMatch = item?.email?.toLowerCase()?.includes(term);
      const nameMatch = fullName.includes(term);

      return {
        ...item,
        display: nameMatch || codeMatch || emailMatch
      };
    });

    setUsersList(updatedList);
  };

  const applyReviewersDispersedSearch = (searchTerm = '', originalList = []) => {
    const term = searchTerm?.toLowerCase();

    const updatedList = originalList?.map((item) => {
      const codeMatch = item?.code?.toLowerCase()?.includes(term);
      const fullName = `${item.firstName} ${item.lastName}`.toLowerCase();
      const emailMatch = item?.email?.toLowerCase()?.includes(term);
      const nameMatch = fullName.includes(term);

      return {
        ...item,
        display: nameMatch || codeMatch || emailMatch
      };
    });

    setReviewers(updatedList);
  };

  const handleUserSearch = (e) => {
    const value = e.target.value;
    setUserInputSearch(value);
    applyUsersDispersedSearch(value, usersList);
  };

  const handleDeleteUserFromList = (e, userCode) => {
    e.stopPropagation();
    e.preventDefault();

    const newList = reviewers?.filter((person) => person?.code !== userCode);
    setReviewers(newList);
  };

  const onDragEnd = async (result) => {
    setIsDragging(false);
    const { source } = result;

    if (!source || source.droppableId !== 'DRAGGABLE_USERS_LIST') {
      return;
    }

    const foundUsers = usersList?.filter((item) => selectedUserIds?.includes(item?.code));
    const newReviewers = [...reviewers, ...foundUsers].filter(
      (obj, index, self) => index === self.findIndex((o) => o.code === obj.code)
    );

    setReviewers(newReviewers);
  };

  const onDragStart = (result) => {
    setIsDragging(true);
  };

  const onConfirm = () => {
    dispatch(updateAssessmentsBuilderReviewers(reviewers || []));
    dispatch(updateFilter({ type: 'assessmentReviewers', open: false }));
    dispatch(updateFilter('assessmentReviewers', {}));
    setIsConfirming(false);
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const retrieveUsers = async (filter = {}) => {
      setIsLoadingUsers(true);
      const isMaestroUser = userRoles.find((role) => role?.id === 'Maestro');
      const notAllowedRoles = ['Student', 'Parent'];

      if (!isMaestroUser) {
        notAllowedRoles.push('Maestro');
      }

      let customFilter = {
        ...filter,
        notInRolesId: notAllowedRoles
      };

      const { data = [] } = await getUsers(customFilter);

      const mappedUsers = data?.map((item) => ({
        ...item,
        display: true
      }));

      setUsersList(mappedUsers);
      setIsLoadingUsers(false);
    };

    const { code, firstName, lastName, preferredName, email, rolesId = [] } = filter;

    if (
      filter &&
      Object.keys(filter).length > 0 &&
      !open &&
      (code || firstName || lastName || preferredName || email || rolesId?.length > 0)
    ) {
      retrieveUsers(filter);
    } else {
      setUsersList([]);
      setSelectedUserIds([]);
      setIsLoadingUsers(false);
    }
  }, [filter]);

  const handleReviewerSearch = (e) => {
    const value = e?.target?.value ?? '';
    setReviewerInputSearch(value);
    applyReviewersDispersedSearch(value, reviewers);
  };

  useEffect(() => {
    if (isConfirming) {
      onConfirm();
    }
  }, [isConfirming]);

  const filteredReviewers = reviewers?.filter((item) => item?.display);

  return (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
      <Box className='global-reviewers-panel'>
        <Box className='global-reviewers-panel__reviewers'>
          <ReviewersPanelSearch
            handleClassSearch={handleReviewerSearch}
            classInputSearch={reviewerInputSearch}
            handleUserSearch={handleUserSearch}
            userInputSearch={userInputSearch}
            users={usersList}
            selectedUserIds={selectedUserIds}
            setSelectedUserIds={setSelectedUserIds}
            assessmentType={ASSESSMENT_TYPE_CUSTOM}
            reviewers={reviewers}
          />
          <Box
            className={`global-reviewers-panel__container  ${
              !filteredReviewers?.length ? 'align-items-center ' : ''
            } ${!isDragging ? ' overflow-y-auto-height ' : ''}`}
          >
            {isMounted && (
              <Droppable droppableId={'DROPPABLE_REVIEWERS_LIST'} isDropDisabled={true}>
                {(provided, snapshot) => {
                  return (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`global-reviewers-panel__droppable`}
                      style={{ width: filteredReviewers?.length === 0 ? '100%' : 'auto' }}
                    >
                      <Box
                        className={`global-reviewers-panel__list-container ${
                          isDragging ? ' dragging-over--active' : ''
                        }`}
                      >
                        {reviewers?.length === 0 ? (
                          <Box className='global-reviewers-panel__no-data align-items-center'>
                            <FontAwesomeIcon
                              icon='users'
                              style={{ width: '3rem', height: '3rem', color: '#afbac3' }}
                            />
                            <p
                              className={` `}
                              style={{ fontSize: '14px', color: '#64748b', fontWeight: '600' }}
                            >
                              No reviewers assigned yet
                            </p>
                            <span
                              className={` `}
                              style={{ fontSize: '12px', color: '#6c7b90', fontWeight: '500' }}
                            >
                              Drop users here from the users panel to assign them as reviewers.
                            </span>
                          </Box>
                        ) : (
                          <Box>
                            <List dense={false} className='global-reviewers-panel__list'>
                              {filteredReviewers?.map((item) => {
                                const roles = item?.roles || [];

                                return (
                                  <ListItem
                                    className='global-reviewers-panel__list-item'
                                    key={item?._id}
                                    secondaryAction={
                                      <IconButton
                                        edge='end'
                                        aria-label='delete'
                                        onClick={(e) => handleDeleteUserFromList(e, item?.code)}
                                        label='Delete Reviewer'
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    }
                                  >
                                    <ListItemAvatar>
                                      <Avatar
                                        size='small'
                                        variant='circular'
                                        alt={item?.code}
                                        src={`photos/${item?.photo}`}
                                      />
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={`${item?.firstName} ${item?.lastName}`}
                                      secondary={
                                        <div
                                          className='flex flex-wrap items-center gap-2 overflow-hidden'
                                          style={{ maxWidth: '100%' }}
                                        >
                                          <span style={{ fontSize: '10px' }}>{item?.code}</span>{' '}
                                          {roles?.map((role) => {
                                            const label = typeof role === 'string' ? role : role?.name;
                                            return (
                                              <Chip
                                                key={label}
                                                label={label}
                                                size='small'
                                                sx={{ margin: '2px' }}
                                              />
                                            );
                                          })}
                                        </div>
                                      }
                                    />
                                  </ListItem>
                                );
                              })}
                            </List>
                          </Box>
                        )}
                      </Box>
                      {provided.placeholder}
                    </div>
                  );
                }}
              </Droppable>
            )}
          </Box>
        </Box>
        <Box className='global-reviewers-panel__users'>
          <ReviewerUsersPanelHeader
            handleClassSearch={handleReviewerSearch}
            classInputSearch={reviewerInputSearch}
            handleUserSearch={handleUserSearch}
            userInputSearch={userInputSearch}
            users={usersList}
            selectedUserIds={selectedUserIds}
            setSelectedUserIds={setSelectedUserIds}
            assessmentType={ASSESSMENT_TYPE_CUSTOM}
            reviewers={reviewers}
          />
          <ReviewerUsersPanel
            users={usersList}
            selectedUserIds={selectedUserIds}
            setSelectedUserIds={setSelectedUserIds}
            isLoadingUsers={isLoadingUsers}
            userInputSearch={userInputSearch}
            reviewers={reviewers}
          />
        </Box>
      </Box>
    </DragDropContext>
  );
};

export default GlobalReviewersPanel;
